import { ColDef, GridReadyEvent, ITooltipParams, ValueGetterParams } from '@ag-grid-community/core';
import { Component } from '@angular/core';
import { BoxSize } from 'app/model/entities/entity-model';
import { Shipment } from 'app/model/entities/shipment';
import { ShippingBox } from 'app/model/entities/shipping-box';
import { Rate } from 'app/model/shipEngine/models';
import { AgFns, GridOptionsExt } from 'app/shared/ag-fns';
import { AgStyleFns } from 'app/shared/ag-style-fns';
import { DialogBaseComponent, DialogParams } from 'app/shared/dialog-base.component';
import { DomainService } from 'app/shared/domain.service';
import { UtilFns } from 'app/shared/util-fns';
import { ShippingCarrier, ShippingService } from 'app/shipping/shipping.service';
import _ from 'lodash';
import moment from 'moment-mini';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InvoiceShipmentService } from './invoice-shipment.service';
import { wrapBusy } from '@/shared/domain-base.component';
import { InvoiceHeader } from 'app/model/entities/invoice-header';
import { JoHeader } from 'app/model/entities/jo-header';
import { ScaleService } from '@/shared/scale.service';

export interface AskShipInfoDialogParams extends DialogParams {
  shipment: Shipment;
}

@Component({
  templateUrl: './ask-ship-info-dialog.component.html',
  standalone: false,
})
export class AskShipInfoDialogComponent extends DialogBaseComponent<AskShipInfoDialogParams> {
  boxWeightGridOptions!: GridOptionsExt;
  uomMap = {
    oz: 'Ounces',
    lb: 'Pounds',
    gr: 'Grams',
    kg: 'Kilograms',
  };
  uoms = Object.entries(this.uomMap).map((x) => {
    return { id: x[0], name: x[1] };
  });
  uomId!: string;
  carriers!: ShippingCarrier[];
  selectedCarrier!: ShippingCarrier;
  selectedService!: { code: string; name: string };
  shipmentApiIdentifier!: string;
  shipment!: Shipment;
  boxes!: ShippingBox[];
  packageMap: any;
  boxSizes!: BoxSize[];
  boxSizeMap: any;
  addressText = '';
  jo!: JoHeader;

  rateGridOptions!: GridOptionsExt;
  rates: Rate[] = [];

  constructor(
    public config: DynamicDialogConfig<AskShipInfoDialogParams>,
    public dialogRef: DynamicDialogRef,
    public domainService: DomainService,
    public shippingService: ShippingService,
    private invoiceShipmentService: InvoiceShipmentService,
    public scaleService: ScaleService,
  ) {
    super(config, dialogRef, domainService);
    Object.assign(this.config, {
      header: 'Enter Shipment Information',
      width: '1400px',
      height: '80vh',
    });
    this.prepare();
  }

  async prepare() {
    this.shipment = this.params.shipment;
    this.jo = this.shipment.invoiceHeaders[0].joHeader;

    this.boxWeightGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onBoxWeightGridReady,
      onCellValueChanged: this.onBoxWeightChanged,
      stopEditingWhenCellsLoseFocus: true,
    });
    this.rateGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onRateGridReady,
      onRowDoubleClicked: this.onRateDoubleClicked,
      onModelUpdated: (p) => p.api?.sizeColumnsToFit(),
      onRowSelected: this.onRateRowSelected,
      getRowId: (params) => {
        return params.data.service_code;
      },
      // pagination: false,
      tooltipShowDelay: 300,
    });

    await this.getCarrierInfo();

    this.boxSizes = await this.dbQueryService.getBoxSizes();
    this.boxSizeMap = Object.fromEntries(
      this.boxSizes.map((bs) => {
        const dims = [bs.length, bs.width, bs.height].filter((d) => d != null).join(' x ');
        return [bs.id, dims ? `${bs.name} (${dims})` : bs.name];
      }),
    );

    this.boxes = this.shipment.shippingBoxes.slice();
    if (this.boxes.length > 0) {
      this.uomId = this.boxes[0].weightUoMId;
    } else {
      // Default to Pounds if available
      this.uomId = this.uoms.find((u) => u.id === 'lb')?.id ?? null;
    }

    this.addressText = UtilFns.getAddress(this.jo, 'shipping');

    this.initRateGridOnOpen();

    this.isPageReady = true;
  }

  async getCarrierInfo() {
    this.carriers = await this.shippingService.getShippingCarriers();

    // Pre-select carrier: use existing carrier if editing, otherwise default to UPS
    if (this.shipment.carrierApiIdentifier) {
      this.selectedCarrier = _.find(this.carriers, (c) => c.code === this.shipment.carrierApiIdentifier);
    } else {
      // Default to UPS if available
      this.selectedCarrier = this.carriers.find((c) => c.name === 'UPS') ?? null;
    }

    if (this.selectedCarrier) {
      this.packageMap = _.mapValues(_.keyBy(this.selectedCarrier.packages, 'code'), 'name');
    } else {
      this.packageMap = {};
    }
  }

  private initRateGridOnOpen() {
    // if shipment already has rate info, pre-populate the rate grid
    if (this.shipment.serviceApiIdentifier) {
      this.selectedService = {
        code: this.shipment.serviceApiIdentifier,
        name: this.shipment.serviceApiIdentifier,
      };
      this.shipmentApiIdentifier = this.shipment.shipmentApiIdentifier;
    }
  }

  onBoxWeightGridReady(event: GridReadyEvent) {
    AgFns.attachGridState(this.boxWeightGridOptions);
    this.boxWeightGridOptions.singleClickEdit = true;
    this.boxWeightGridOptions.stopEditingWhenCellsLoseFocus = true;

    const colDefs = [
      AgStyleFns.getEntityDeleteColDef(this.deleteBox.bind(this)),
      {
        headerName: 'Box Id',
        editable: false,
        ...AgFns.fixWidth(140),
        valueGetter: (params: any) => {
          const sb = <ShippingBox>params.data;
          return sb.id > 0 ? sb.id : 'New box' + sb.id;
        },
      },
      {
        headerName: 'Box Size',
        field: 'boxSizeId',
        editable: true,
        ...AgFns.fixWidth(240),
        ...AgFns.createDropdownEditorPropsFromMapObj(this.boxSizeMap),
        valueSetter: (params: any) => {
          const sb = params.data as ShippingBox;
          const newBoxSizeId = +params.newValue;
          sb.boxSizeId = newBoxSizeId;
          if (newBoxSizeId) {
            const boxSize = this.boxSizes.find((bs) => bs.id === newBoxSizeId);
            if (boxSize) {
              sb.length = boxSize.length;
              sb.width = boxSize.width;
              sb.height = boxSize.height;
            }
          }
          params.api.refreshCells({ rowNodes: [params.node], force: true });
          this.clearRates();
          return true;
        },
      },
      { headerName: 'Weight', field: 'weight', editable: true, ...AgFns.fixWidth(120) },
      { headerName: 'L (in)', field: 'length', editable: true, ...AgFns.fixWidth(100) },
      { headerName: 'W (in)', field: 'width', editable: true, ...AgFns.fixWidth(100) },
      { headerName: 'H (in)', field: 'height', editable: true, ...AgFns.fixWidth(100) },
      {
        headerName: 'Package',
        field: 'packageApiIdentifier',
        editable: true,
        ...AgFns.createDropdownEditorPropsFromMapObj(this.packageMap),
      },
    ];

    AgFns.initGrid(event, colDefs);
    AgFns.sizeColumnsToFit(this.boxWeightGridOptions);
  }

  onBoxWeightChanged(event) {
    this.clearRates();
  }

  onRateGridReady(event: GridReadyEvent) {
    AgFns.attachGridState(this.rateGridOptions);

    const colDefs: ColDef[] = [
      { field: 'carrier_friendly_name', headerName: 'Carrier', width: 70 },
      {
        field: 'service_type',
        headerName: 'Service',
        width: 310,
        valueGetter: (params: ValueGetterParams) => {
          const rate = params.data as Rate;
          return rate.service_type + ' (' + (rate.package_type || rate.service_code) + ')';
        },
        tooltipValueGetter: (params: ITooltipParams) => {
          const rate = params.data as Rate;
          return rate.service_code + (rate.package_type ? ': ' + rate.package_type : '');
        },
      },
      {
        field: 'delivery_days',
        headerName: 'Delivery Days',
        width: 80,
        type: 'numericColumn',
        tooltipValueGetter: (params: ITooltipParams) => {
          const cdd = params.data.carrier_delivery_days;
          return cdd && cdd.length > 3 ? cdd : moment(params.data.estimated_delivery_date).format('dddd, M/D, h:mm a');
        },
      },
      {
        field: 'amount',
        headerName: 'Amount',
        ...AgFns.fixWidth(120),
        type: 'numericColumn',
        valueFormatter: (params) => UtilFns.fmtCurrency(params.value),
        valueGetter: (params: ValueGetterParams) => {
          const rate = params.data as Rate;
          const amount =
            rate.shipping_amount.amount + rate.insurance_amount.amount + rate.confirmation_amount.amount + rate.other_amount.amount;
          return +amount.toFixed(2);
        },
      },
      {
        colId: 'markup',
        headerName: '(+) Markup',
        ...AgFns.fixWidth(120),
        type: 'numericColumn',
        valueFormatter: (params) => UtilFns.fmtCurrency(params.value),
        valueGetter: (params: ValueGetterParams) => {
          const rate = params.data as Rate;
          const amount =
            rate.shipping_amount.amount + rate.insurance_amount.amount + rate.confirmation_amount.amount + rate.other_amount.amount;
          const ratio = InvoiceHeader.freightMarkupRatio ?? 0;
          return +(amount * ratio).toFixed(2);
        },
      },
      {
        colId: 'total',
        headerName: 'Total',
        ...AgFns.fixWidth(120),
        type: 'numericColumn',
        sort: 'asc',
        valueFormatter: (params) => UtilFns.fmtCurrency(params.value),
        valueGetter: (params: ValueGetterParams) => {
          const rate = params.data as Rate;
          const amount =
            rate.shipping_amount.amount + rate.insurance_amount.amount + rate.confirmation_amount.amount + rate.other_amount.amount;
          const ratio = InvoiceHeader.freightMarkupRatio ?? 0;
          return +(amount * (1 + ratio)).toFixed(2);
        },
      },
    ];

    AgFns.initGrid(event, colDefs);
  }

  onRateRowSelected(event) {
    if (!event.node.selected) {
      return;
    }
    const rate = event.data as Rate;
    this.selectedCarrier = this.carriers.find((c) => c.code === rate.carrier_id);
    this.selectedService = { code: rate.service_code, name: rate.service_type };
  }

  onRateDoubleClicked(event) {
    this.ok();
  }

  onCarrierChange(newCarrier: ShippingCarrier) {
    this.selectedCarrier = newCarrier;
    this.packageMap = _.mapValues(_.keyBy(this.selectedCarrier.packages, 'code'), 'name');
    this.boxes.forEach((b) => (b.packageApiIdentifier = null));

    if (this.boxWeightGridOptions.apiX) {
      const colDefs = this.boxWeightGridOptions.apiX.getColumnDefs();
      if (colDefs) {
        const cd = colDefs?.find((cd: ColDef) => cd.field === 'packageApiIdentifier');
        Object.assign(cd, {
          editable: true,
          ...AgFns.createDropdownEditorPropsFromMapObj(this.packageMap),
        });
        this.boxWeightGridOptions.apiX.setGridOption('columnDefs', colDefs);
        this.boxWeightGridOptions.apiX.redrawRows();
      }
    }
    this.clearRates();
  }

  onUomChange(newUomId: string) {
    this.uomId = newUomId;
    this.shipment.shippingBoxes.forEach((sb) => (sb.weightUoMId = newUomId));
    this.clearRates();
  }

  addBox() {
    const newBox = this.dbSaveService.uow.createEntity(ShippingBox, {
      shipmentId: this.shipment.id,
      weightUoMId: this.uomId,
    });
    this.boxes = this.shipment.shippingBoxes.slice();

    this.clearRates();
  }

  deleteBox(box: ShippingBox) {
    box.entityAspect.setDeleted();
    this.boxes = this.shipment.shippingBoxes.slice();
    this.clearRates();
  }

  private clearRates() {
    this.rates = [];
    this.selectedService = null;
  }

  canGetRates() {
    return this.selectedCarrier && this.uomId && this.shipment.shippingBoxes.length && this.shipment.shippingBoxes.every((x) => x.weight);
  }

  canAddBox() {
    return !!this.uomId;
  }

  canReadScale() {
    return !!this.uomId && this.boxes.length > 0;
  }

  async readScale() {
    try {
      const connected = await this.scaleService.connect();
      if (!connected) {
        return;
      }

      const reading = await this.scaleService.readWeight();

      if (!reading.stable) {
        this.toastr.warning('Scale reading is not stable. Place the item and wait.', 'Scale');
        return;
      }

      if (reading.weight <= 0) {
        this.toastr.warning('Scale shows zero or negative weight. Place an item on the scale.', 'Scale');
        return;
      }

      const convertedWeight = ScaleService.convertWeight(reading.weight, reading.unit, this.uomId);

      // Fill the selected row, or the last box if none selected
      const selectedRows = this.boxWeightGridOptions.apiX?.getSelectedRows() as ShippingBox[];
      const targetBox = selectedRows?.length > 0 ? selectedRows[0] : this.boxes[this.boxes.length - 1];

      if (!targetBox) {
        this.toastr.warning('No box available. Add a box first.', 'Scale');
        return;
      }

      targetBox.weight = convertedWeight;
      this.boxWeightGridOptions.apiX?.refreshCells({ force: true });
      this.clearRates();
      this.toastr.success(`Weight: ${convertedWeight} ${this.uomMap[this.uomId]}`, 'Scale');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to read scale';
      this.toastr.error(msg, 'Scale Error');
    }
  }

  canClickOk() {
    return this.selectedService && this.shipmentApiIdentifier && this.rates.length && this.shipment.shippingBoxes.length;
  }

  async ok() {
    this.boxWeightGridOptions.apiX.stopEditing();
    const boxes = this.shipment.shippingBoxes;
    if (boxes.length == 0) {
      this.dialogService.showOkMessage('No boxes found', 'You must have at least one box.');
      return;
    }
    if (!boxes.every((sb) => sb.weight > 0)) {
      this.dialogService.showOkMessage('Invalid Weight', 'All boxes must have weights greater than 0.');
      return;
    }

    this.shipment.carrierApiIdentifier = this.selectedCarrier.code;
    this.shipment.serviceApiIdentifier = this.selectedService.code;
    // The rate-shop lookup sends no service_code, so its shipment id cannot buy a label
    // ("service_code is required when carrier_id is provided"). askAndCalcFreight sets the real id
    // from its per-service getRate; until then a null id makes Print Shipping Label stop and ask.
    this.shipment.shipmentApiIdentifier = null;

    const rate = _.find(this.rates, (r) => r.service_code === this.selectedService.code);

    // NOTE: currency is assumed to be $
    this.shipment.freightAmt =
      rate.shipping_amount.amount + rate.insurance_amount.amount + rate.confirmation_amount.amount + rate.other_amount.amount;

    await this.dbSaveService.saveChanges();

    this.dialogRef.close(true);
  }

  cancel() {
    this.dbSaveService.rejectChanges();
    this.dialogRef.close(null);
  }

  @wrapBusy
  async lookupRates() {
    await AgFns.waitForGrid(this.rateGridOptions);
    await AgFns.busyGrid(this.rateGridOptions, this, async () => {
      const ratesResponse = await this.invoiceShipmentService.getRatesForInvoice(
        this.shipment.invoiceHeaders[0],
        this.selectedCarrier.code,
        undefined,
      );
      this.rates = ratesResponse.rates ?? [];
      this.shipmentApiIdentifier = ratesResponse.shipmentApiIdentifier;
    });
  }
}
